/**
 * Dashboard — Quick Stats Cards row (Journal Entries, Trial Balance, Current Period, Accounts)
 */

import type { ReactNode } from 'react'

import { Card, Skeleton } from '@elsapiens/ui'
import { Link } from 'react-router-dom'

import { getPeriodIconColorClass } from './dashboard-helpers'
import { formatMoney } from '../../utils/formatters'

import type { FiscalPeriod } from '../../services/fiscal-period-service'
import type { TrialBalanceReport } from '../../services/reporting-service'

interface QuickStatsCardsProps {
  isLoading: boolean
  recentEntriesCount: number
  trialBalance: TrialBalanceReport | null
  currentPeriod: FiscalPeriod | null
  hasFiscalPeriods: boolean | null
  accountCount: number
}

interface StatCardProps {
  to: string
  title: string
  isLoading: boolean
  skeletonWidth: string
  primary: ReactNode
  secondary?: ReactNode
  primaryClass?: string
  iconClass: string
  iconPath: string
}

function StatCard({
  to, title, isLoading, skeletonWidth, primary, secondary, primaryClass, iconClass, iconPath,
}: StatCardProps): JSX.Element {
  return (
    <Link to={to}>
      <Card className="h-full hover:shadow-md transition-shadow" padding="sm">
        <div className="flex items-center justify-between" style={{ minHeight: '3.5rem' }}>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-secondary truncate">{title}</p>
            {isLoading ? (
              <Skeleton className="el-mt-xs" height="1.5rem" width={skeletonWidth} />
            ) : (
              <p className={`text-xl font-semibold mt-0.5 truncate ${primaryClass ?? ''}`.trim()}>
                {primary}
              </p>
            )}
            {!isLoading && secondary !== undefined && (
              <p className="text-xs text-foreground-secondary mt-0.5 truncate">{secondary}</p>
            )}
          </div>
          <div className={`p-2.5 rounded-lg flex-shrink-0 ml-3 ${iconClass}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d={iconPath} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          </div>
        </div>
      </Card>
    </Link>
  )
}

const ICON_JOURNAL = 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
const ICON_TRIAL = 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3'
const ICON_PERIOD = 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
const ICON_ACCOUNTS = 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'

function buildStatCards(props: QuickStatsCardsProps): StatCardProps[] {
  const { isLoading, recentEntriesCount, trialBalance, currentPeriod, hasFiscalPeriods, accountCount } = props
  const trialBalanceClass = trialBalance?.isBalanced
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400'
  const trialIconClass = trialBalance?.isBalanced
    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
    : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
  const periodName = currentPeriod?.name ?? (hasFiscalPeriods === false ? 'Not configured' : 'Not set')
  const trialBalanceSecondary = trialBalance ? (
    <>
      {formatMoney(trialBalance.totalDebit)} DR &bull; {formatMoney(trialBalance.totalCredit)} CR
    </>
  ) : undefined
  return [
    {
      to: '/journal', title: 'Journal Entries', isLoading, skeletonWidth: '80px',
      primary: recentEntriesCount > 0 ? `${recentEntriesCount} recent` : 'No entries',
      secondary: 'View all transactions',
      iconClass: 'bg-accent text-primary', iconPath: ICON_JOURNAL,
    },
    {
      to: '/reports/trial-balance', title: 'Trial Balance', isLoading, skeletonWidth: '80px',
      primary: trialBalance?.isBalanced ? 'Balanced' : 'Check Required',
      primaryClass: trialBalanceClass,
      secondary: trialBalanceSecondary,
      iconClass: trialIconClass, iconPath: ICON_TRIAL,
    },
    {
      to: '/periods', title: 'Current Period', isLoading, skeletonWidth: '100px',
      primary: periodName,
      secondary: currentPeriod ? `${currentPeriod.startDate} - ${currentPeriod.endDate}` : undefined,
      iconClass: getPeriodIconColorClass(hasFiscalPeriods, currentPeriod !== null),
      iconPath: ICON_PERIOD,
    },
    {
      to: '/accounts', title: 'Accounts', isLoading, skeletonWidth: '80px',
      primary: accountCount > 0 ? `${accountCount} accounts` : 'View All',
      secondary: 'Active in current ledger',
      iconClass: 'bg-accent text-primary', iconPath: ICON_ACCOUNTS,
    },
  ]
}

export function QuickStatsCards(props: QuickStatsCardsProps): JSX.Element {
  const cards = buildStatCards(props)
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 el-gap-md el-mb-lg">
      {cards.map(card => <StatCard key={card.to} {...card} />)}
    </div>
  )
}
